import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Link2, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../../lib/utils';
import { portfolioHooks, sectorHooks } from '../Org/hooks';
import { DUE_OPTIONS, EMPTY_FILTER, isDefaultFilter, NO_OWNER, STATUS_OPTIONS, statesLabel } from './filters';
import { ViewPicker } from './ViewPicker';
/** A compact select that shows "All …" when off and tints itself when on. */ function FilterSelect({ testid, label, allLabel, value, options, onChange }) {
    // A value from the URL with no option yet (lists still loading) keeps its place.
    const known = !value || options.some((o)=>o.id === value);
    return /*#__PURE__*/ _jsxs(SpecraSelect, {
        "aria-label": label,
        className: cn('w-auto max-w-xs', value && 'border-brand bg-brand-muted text-brand'),
        "data-testid": testid,
        onChange: (e)=>onChange(e.target.value),
        size: "sm",
        value: value,
        children: [
            /*#__PURE__*/ _jsx("option", {
                value: "",
                children: allLabel
            }),
            !known && /*#__PURE__*/ _jsxs("option", {
                value: value,
                children: [
                    label,
                    ": selected"
                ]
            }),
            options.map((o)=>/*#__PURE__*/ _jsx("option", {
                    value: o.id,
                    children: o.label
                }, o.id))
        ]
    });
}
const one = (v)=>v ? [
        v
    ] : [];
const RANGE = 'range';
const short = (iso)=>new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC'
    });
/** The Due filter's words for a drill's exact days. */ function rangeLabel({ from, to }) {
    if (from && to) return from === to ? `Due ${short(from)}` : `Due ${short(from)} – ${short(to)}`;
    if (to) return `Due by ${short(to)}`;
    return `Due from ${short(from)}`;
}
export function CommandCenterFilterBar({ companyId, filter, onChange, programs, teams, owners, onShare }) {
    const [orgOpen, setOrgOpen] = useState(false);
    const showOrg = orgOpen || !!filter.portfolio || !!filter.sector;
    const set = (patch)=>onChange({
            ...filter,
            ...patch
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "sticky top-0 z-10 -mx-4 space-y-2 border-b border-border bg-background px-4 py-2 sm:-mx-6 sm:px-6",
        "data-testid": Testids.commandCenter.filterBar,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(FilterSelect, {
                        allLabel: `All programs (${programs.length})`,
                        label: "Program",
                        onChange: (v)=>set({
                                programs: one(v),
                                teams: []
                            }),
                        options: programs,
                        testid: Testids.commandCenter.filterProgram,
                        value: filter.programs[0] ?? ''
                    }),
                    /*#__PURE__*/ _jsx(FilterSelect, {
                        allLabel: "All teams",
                        label: "Team",
                        onChange: (v)=>set({
                                teams: one(v)
                            }),
                        options: teams.map((t)=>({
                                id: t.id,
                                label: t.programName ? `${t.name} · ${t.programName}` : t.name
                            })),
                        testid: Testids.commandCenter.filterTeam,
                        value: filter.teams[0] ?? ''
                    }),
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Period",
                        className: cn('w-auto', filter.period === 'all' && 'border-brand bg-brand-muted text-brand'),
                        "data-testid": Testids.commandCenter.filterPeriod,
                        onChange: (e)=>set({
                                period: e.target.value
                            }),
                        size: "sm",
                        value: filter.period,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "current",
                                children: "Current period"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: "all",
                                children: "All dates"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FilterSelect, {
                        allLabel: "Any status",
                        label: "Status",
                        onChange: (v)=>set({
                                states: v ? v.split(',') : []
                            }),
                        options: [
                            // A chart's drill can set several statuses at once ("Filed").
                            ...filter.states.length > 1 ? [
                                {
                                    id: filter.states.join(','),
                                    label: statesLabel(filter.states)
                                }
                            ] : [],
                            ...STATUS_OPTIONS.map((o)=>({
                                    id: o.value,
                                    label: o.label
                                }))
                        ],
                        testid: Testids.commandCenter.filterStatus,
                        value: filter.states.join(',')
                    }),
                    /*#__PURE__*/ _jsx(FilterSelect, {
                        allLabel: "Any owner",
                        label: "Owner",
                        onChange: (v)=>set({
                                owners: one(v)
                            }),
                        options: [
                            ...owners.map((o)=>({
                                    id: o.id,
                                    label: o.name || 'Unnamed contact'
                                })),
                            {
                                id: NO_OWNER,
                                label: 'No owner'
                            }
                        ],
                        testid: Testids.commandCenter.filterOwner,
                        value: filter.owners[0] ?? ''
                    }),
                    /*#__PURE__*/ _jsx(FilterSelect, {
                        allLabel: "Any due date",
                        label: "Due",
                        onChange: (v)=>v === RANGE ? undefined : set({
                                due: v,
                                from: '',
                                to: ''
                            }),
                        options: [
                            // A chart's drill sets exact days.
                            ...filter.from || filter.to ? [
                                {
                                    id: RANGE,
                                    label: rangeLabel(filter)
                                }
                            ] : [],
                            ...DUE_OPTIONS.map((o)=>({
                                    id: o.value,
                                    label: o.label
                                }))
                        ],
                        testid: Testids.commandCenter.filterDue,
                        value: filter.from || filter.to ? RANGE : filter.due
                    }),
                    !showOrg && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.commandCenter.filterMore,
                        iconLeft: /*#__PURE__*/ _jsx(Plus, {}),
                        onClick: ()=>setOrgOpen(true),
                        size: "sm",
                        variant: "ghost",
                        children: "Portfolio, sector"
                    }),
                    !isDefaultFilter(filter) && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.commandCenter.filterClear,
                        iconLeft: /*#__PURE__*/ _jsx(X, {}),
                        onClick: ()=>onChange(EMPTY_FILTER),
                        size: "sm",
                        variant: "ghost",
                        children: "Clear"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "hidden min-w-0 flex-1 sm:block"
                    }),
                    /*#__PURE__*/ _jsx(ViewPicker, {
                        filter: filter,
                        onChange: onChange
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.commandCenter.share,
                        iconLeft: /*#__PURE__*/ _jsx(Link2, {}),
                        onClick: onShare,
                        size: "sm",
                        variant: "secondary",
                        children: "Share"
                    })
                ]
            }),
            showOrg && /*#__PURE__*/ _jsx(OrgFilters, {
                companyId: companyId,
                filter: filter,
                onChange: set
            })
        ]
    });
}
/** Portfolio and sector, mounted only when asked for, so their lists cost nothing until then. */ function OrgFilters({ companyId, filter, onChange }) {
    const portfolios = portfolioHooks.useList({
        companyId,
        businessUnitId: ''
    });
    const sectors = sectorHooks.useList({
        companyId
    });
    const options = (items)=>(items ?? []).map((i)=>({
                id: i.id,
                label: i.name
            }));
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        children: [
            /*#__PURE__*/ _jsx(FilterSelect, {
                allLabel: "All portfolios",
                label: "Portfolio",
                onChange: (v)=>onChange({
                        portfolio: v
                    }),
                options: options(portfolios.data?.items),
                testid: Testids.commandCenter.filterPortfolio,
                value: filter.portfolio
            }),
            /*#__PURE__*/ _jsx(FilterSelect, {
                allLabel: "All sectors",
                label: "Sector",
                onChange: (v)=>onChange({
                        sector: v
                    }),
                options: options(sectors.data?.items),
                testid: Testids.commandCenter.filterSector,
                value: filter.sector
            })
        ]
    });
}
